import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Shared";
import { Headphones, LogOut, Search, UserCog, Eye, ScrollText } from "lucide-react";
import { toast } from "sonner";

export default function SupportDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("users");

  const load = async (query = "") => {
    try {
      const [u, o, l] = await Promise.all([
        api.get("/support/users", { params: query ? { q: query } : {} }),
        api.get("/support/organizations"),
        api.get("/support/audit-logs"),
      ]);
      setUsers(u.data); setOrgs(o.data); setLogs(l.data);
    } catch { toast.error("Laden mislukt"); }
  };
  useEffect(() => { load(); }, []);

  const search = (e) => { e.preventDefault(); load(q); };

  const impersonate = async (uid, name) => {
    if (!confirm(`Inloggen als "${name}"? Deze actie wordt gelogd.`)) return;
    try {
      const { data } = await api.post(`/support/impersonate/${uid}`);
      if (data.access_token) localStorage.setItem("access_token", data.access_token);
      localStorage.setItem("stockly_impersonating", "1");
      toast.success(`Ingelogd als ${name}`);
      // Route by target role
      if (data.user.role === "super_admin") nav("/platform");
      else nav("/");
      window.location.reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Mislukt"); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="support-dashboard">
      <header className="sticky top-0 z-20 bg-white border-b border-zinc-200 px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Headphones size={20} className="text-blue-600" />
          <div className="font-bold tracking-tight">Stockly · Support</div>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-zinc-500 hidden sm:inline">{user?.name}</span>
          <button onClick={async () => { await logout(); nav("/login"); }} className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900" data-testid="logout-button">
            <LogOut size={15} /> Uitloggen
          </button>
        </div>
      </header>

      <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
        <PageHeader
          overline="Support"
          title="Klanten helpen"
          description="Zoek gebruikers, log tijdelijk in als klant, of bekijk je audit log."
        />

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <Stat label="Organisaties" value={orgs.length} />
          <Stat label="Gebruikers gevonden" value={users.length} />
          <Stat label="Mijn audit events" value={logs.length} />
        </div>

        <div className="flex items-center gap-2 mb-4">
          <TabBtn active={tab === "users"} onClick={() => setTab("users")} testid="tab-users">Gebruikers</TabBtn>
          <TabBtn active={tab === "logs"}  onClick={() => setTab("logs")}  testid="tab-logs">Mijn audit logs</TabBtn>
        </div>

        {tab === "users" && (
          <>
            <form onSubmit={search} className="mb-4 flex gap-2">
              <div className="relative flex-1 max-w-md">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek op naam of e-mail…" className="w-full h-10 pl-9 pr-3 border border-zinc-300 rounded-md text-sm bg-white" data-testid="search-users" />
              </div>
              <button type="submit" className="h-10 px-4 bg-[#1B2D5C] hover:bg-[#142244] text-white rounded-md text-sm font-semibold" data-testid="search-submit">Zoeken</button>
            </form>

            <div className="card-flat overflow-x-auto">
              <table className="w-full text-sm min-w-[760px]">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr className="text-left">
                    <th className="px-4 py-3 overline">Gebruiker</th>
                    <th className="px-4 py-3 overline">Rol</th>
                    <th className="px-4 py-3 overline">Organisatie</th>
                    <th className="px-4 py-3 overline text-right">Acties</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {users.length === 0 ? (
                    <tr><td colSpan="4" className="px-4 py-8 text-center text-sm text-zinc-500">Geen gebruikers gevonden.</td></tr>
                  ) : users.map((u) => (
                    <tr key={u.id} className="hover:bg-zinc-50" data-testid={`user-row-${u.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold">{u.name}</div>
                        <div className="text-xs text-zinc-500 font-mono">{u.email}</div>
                      </td>
                      <td className="px-4 py-3"><RoleBadge role={u.role} /></td>
                      <td className="px-4 py-3 text-sm">{u.organization_name || "—"}</td>
                      <td className="px-4 py-3 text-right">
                        {u.role !== "super_admin" && u.id !== user?.id && (
                          <button onClick={() => impersonate(u.id, u.name)} className="inline-flex items-center gap-1.5 h-8 px-3 bg-amber-500 hover:bg-amber-600 text-white rounded text-xs font-semibold" data-testid={`impersonate-${u.id}`}>
                            <UserCog size={13} /> Inloggen als
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "logs" && (
          <div className="card-flat overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-left">
                  <th className="px-4 py-3 overline">Wanneer</th>
                  <th className="px-4 py-3 overline">Actie</th>
                  <th className="px-4 py-3 overline">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {logs.length === 0 ? (
                  <tr><td colSpan="3" className="px-4 py-8 text-center text-sm text-zinc-500">Nog geen audit events.</td></tr>
                ) : logs.map((l) => (
                  <tr key={l.id} data-testid={`log-row-${l.id}`}>
                    <td className="px-4 py-3 text-xs font-mono text-zinc-500">{new Date(l.created_at).toLocaleString("nl-NL")}</td>
                    <td className="px-4 py-3"><span className="px-2 py-1 bg-zinc-100 rounded text-xs font-mono">{l.action}</span></td>
                    <td className="px-4 py-3 text-xs text-zinc-600">
                      {l.action === "impersonate.start"
                        ? `→ ${l.details?.target_name} (${l.details?.target_email})`
                        : (l.details ? JSON.stringify(l.details).slice(0, 140) : "")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="card-flat p-5">
      <div className="overline">{label}</div>
      <div className="text-3xl font-bold tracking-tight mt-1 font-mono">{value}</div>
    </div>
  );
}
function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} className={`h-10 px-4 rounded-md text-sm font-semibold transition-colors ${active ? "bg-[#1B2D5C] text-white" : "bg-white border border-zinc-200 text-zinc-700 hover:border-[#5BC044]"}`} data-testid={testid}>{children}</button>
  );
}
function RoleBadge({ role }) {
  const map = {
    super_admin: "bg-purple-100 text-purple-700",
    support: "bg-blue-100 text-blue-700",
    organization_admin: "bg-emerald-100 text-emerald-700",
    admin: "bg-emerald-100 text-emerald-700",
    chef: "bg-amber-100 text-amber-700",
  };
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${map[role] || "bg-zinc-100 text-zinc-700"}`}>{role}</span>;
}
